import React, { useState, useEffect } from 'react';
import {
  collection, addDoc, getDocs, deleteDoc,
  doc, updateDoc, query, orderBy, setDoc, getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const AdminPortal = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [view, setView] = useState('products');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });

  const [deleteModal, setDeleteModal] = useState({ show: false, type: '', target: '', replacement: 'none' });
  const [editMode, setEditMode] = useState({ type: null, original: '', current: '' });
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [previewSlideIdx, setPreviewSlideIdx] = useState(0);

  const [pageConfig, setPageConfig] = useState({
    heroSlides: [], whatsappNumber: '', categories: [], subCategories: [], faqs: [], contacts: []
  });

  const [form, setForm] = useState({
    name: '', categories: [], subCategories: [], description: '',
    variants: [{ color: 'Estándar', images: [] }]
  });

  const [newContact, setNewContact] = useState({ title: '', icon: 'whatsapp', value: '', link: '' });
  const [newCatName, setNewCatName] = useState('');
  const [newSubName, setNewSubName] = useState('');
  const [newFAQ, setNewFAQ] = useState({ question: '', answer: '' });

  useEffect(() => {
    fetchProducts();
    fetchPageConfig();
  }, []);

  const showMsg = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const fetchProducts = async () => {
    const q = query(collection(db, "products"), orderBy("name", "asc"));
    const snap = await getDocs(q);
    setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const fetchPageConfig = async () => {
    try {
      const docRef = doc(db, "settings", "home");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) setPageConfig(docSnap.data());
    } catch (error) { console.error(error); }
  };

  const handleEditName = async (type) => {
    if (!editMode.current.trim() || editMode.current === editMode.original) {
      setEditMode({ type: null, original: '', current: '' });
      return;
    }
    setLoading(true);
    try {
      const field = type === 'category' ? 'categories' : 'subCategories';
      const newList = pageConfig[field].map(item => item === editMode.original ? editMode.current.trim().toUpperCase() : item);
      const newConfig = { ...pageConfig, [field]: newList };
      await setDoc(doc(db, "settings", "home"), newConfig);

      const batchUpdates = products.filter(p => p[field]?.includes(editMode.original));
      await Promise.all(batchUpdates.map(p => {
        const updatedTags = (p[field] || []).map(t => t === editMode.original ? editMode.current.trim().toUpperCase() : t);
        return updateDoc(doc(db, "products", p.id), { [field]: updatedTags });
      }));
      setPageConfig(newConfig);
      fetchProducts();
      showMsg("Actualización global completada", "success");
    } catch (e) { showMsg("Error al editar", "error"); }
    setEditMode({ type: null, original: '', current: '' });
    setLoading(false);
  };

  const handleSmartDelete = async () => {
    setLoading(true);
    try {
      const { type, target, replacement } = deleteModal;
      const field = type === 'category' ? 'categories' : 'subCategories';
      const newList = pageConfig[field].filter(item => item !== target);
      const newConfig = { ...pageConfig, [field]: newList };
      await setDoc(doc(db, "settings", "home"), newConfig);

      const affectedProducts = products.filter(p => p[field]?.includes(target));
      await Promise.all(affectedProducts.map(p => {
        let tags = (p[field] || []).filter(t => t !== target);
        if (replacement !== 'none' && !tags.includes(replacement)) tags.push(replacement);
        return updateDoc(doc(db, "products", p.id), { [field]: tags });
      }));
      setPageConfig(newConfig);
      fetchProducts();
      setDeleteModal({ show: false, type: '', target: '', replacement: 'none' });
      showMsg("Etiqueta eliminada y productos reasignados", "success");
    } catch (e) { showMsg("Error al borrar", "error"); }
    setLoading(false);
  };

  const handleDeleteFullProduct = async (p) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${p.name}"? Se borrarán también todas sus imágenes.`)) return;
    setLoading(true);
    try {
      if (p.variants) {
        for (const v of p.variants) {
          await Promise.all((v.images || []).map(async (url) => {
            try { await deleteObject(ref(storage, url)); } catch (e) { console.error(e); }
          }));
        }
      }
      await deleteDoc(doc(db, "products", p.id));
      fetchProducts();
      showMsg("Producto eliminado", "success");
    } catch (err) { showMsg("Error al eliminar", "error"); }
    setLoading(false);
  };

  const toggleTag = (list, item, field) => {
    const newList = list.includes(item) ? list.filter(i => i !== item) : [...list, item];
    setForm({ ...form, [field]: newList });
  };

  const handleDragStart = (index) => setDraggedIdx(index);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (index) => {
    if (draggedIdx === null) return;
    const newSlides = [...pageConfig.heroSlides];
    const item = newSlides.splice(draggedIdx, 1)[0];
    newSlides.splice(index, 0, item);
    setPageConfig({ ...pageConfig, heroSlides: newSlides });
    setDraggedIdx(null);
  };

  const handleImageUpload = async (vIndex, e, isHero = false, slideIdx = null) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setLoading(true);
    try {
      const urls = await Promise.all(files.map(async (file) => {
        const path = isHero ? `hero/${Date.now()}-${file.name}` : `products/${Date.now()}-${file.name}`;
        const sRef = ref(storage, path);
        await uploadBytes(sRef, file);
        return await getDownloadURL(sRef);
      }));
      if (isHero) {
        const newSlides = [...pageConfig.heroSlides];
        newSlides[slideIdx].background = urls[0];
        setPageConfig({ ...pageConfig, heroSlides: newSlides });
      } else {
        const newVariants = [...form.variants];
        newVariants[vIndex].images = [...newVariants[vIndex].images, ...urls];
        setForm({ ...form, variants: newVariants });
      }
      showMsg("Imagen cargada", "success");
    } catch (err) { showMsg("Error de subida", "error"); }
    setLoading(false);
  };

  const handleRemoveImage = async (vIndex, imgIndex, url) => {
    if (window.confirm("¿Eliminar imagen?")) {
      try { await deleteObject(ref(storage, url)); } catch (e) { }
      const newVariants = [...form.variants];
      newVariants[vIndex].images.splice(imgIndex, 1);
      setForm({ ...form, variants: newVariants });
    }
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    if (form.categories.length === 0) return showMsg("Selecciona una categoría", "error");
    setLoading(true);
    try {
      if (editingId) await updateDoc(doc(db, "products", editingId), form);
      else await addDoc(collection(db, "products"), form);
      setForm({ name: '', categories: [], subCategories: [], description: '', variants: [{ color: 'Estándar', images: [] }] });
      setEditingId(null);
      fetchProducts();
      showMsg("Producto guardado", "success");
    } catch (err) { showMsg("Error al guardar", "error"); }
    setLoading(false);
  };

  const updateSlide = (index, field, value) => {
    const newSlides = [...pageConfig.heroSlides];
    newSlides[index][field] = value;
    setPageConfig({ ...pageConfig, heroSlides: newSlides });
  };

  const saveGlobalConfig = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, "settings", "home"), pageConfig);
      showMsg("Web actualizada", "success");
    } catch (e) { showMsg("Error al publicar", "error"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-slate-800 flex flex-col md:flex-row font-sans selection:bg-[#a6192e] selection:text-white overflow-x-hidden">

      <aside className="fixed bottom-0 left-0 right-0 z-[150] bg-white border-t border-gray-200 md:relative md:w-64 md:border-r md:h-screen p-4 md:p-8 flex md:flex-col shadow-2xl md:shadow-none">
        <h2 className="hidden md:block text-2xl font-black text-[#a6192e] mb-12 tracking-tighter italic uppercase leading-none text-slate-800">Grupo<span className="text-[#a6192e]">Diana</span></h2>
        <nav className="flex md:flex-col w-full gap-2 justify-around md:justify-start md:flex-1">
          <button onClick={() => setView('products')} className={`flex-1 md:flex-none p-3 md:p-4 rounded-2xl transition-all ${view === 'products' ? 'bg-[#a6192e] text-white font-bold shadow-lg' : 'text-slate-400 hover:bg-gray-100'}`}>Insumos</button>
          <button onClick={() => setView('config')} className={`flex-1 md:flex-none p-3 md:p-4 rounded-2xl transition-all ${view === 'config' ? 'bg-[#a6192e] text-white font-bold shadow-lg' : 'text-slate-400 hover:bg-gray-100'}`}>Ajustes</button>
          <button onClick={() => { logout(); navigate('/login'); }} className="flex-1 md:flex-none p-3 md:p-4 rounded-2xl text-slate-400 hover:text-red-600 transition-colors">Salir</button>
        </nav>
      </aside>

      <main className="flex-1 p-4 md:p-12 pb-32 md:pb-12">
        <AnimatePresence>
          {message.text && (
            <motion.div initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -50, opacity: 0 }} className={`fixed top-5 right-5 z-[200] px-8 py-4 rounded-2xl shadow-2xl border-l-4 ${message.type === 'success' ? 'bg-white border-green-500 text-green-600' : 'bg-white border-red-500 text-red-600 font-bold'}`}>
              {message.text}
            </motion.div>
          )}

          {deleteModal.show && (
            <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-sm">
              <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-white p-8 rounded-[2rem] shadow-2xl max-w-md w-full text-slate-800">
                <h3 className="text-xl font-black text-[#a6192e] uppercase italic mb-4 leading-none">Borrado Inteligente</h3>
                <p className="text-slate-500 text-sm mb-6 leading-relaxed italic">Vas a eliminar <b>"{deleteModal.target}"</b>. ¿A qué etiqueta reasignamos los productos vinculados?</p>
                <select className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl text-xs font-bold mb-8 outline-none text-slate-800" value={deleteModal.replacement} onChange={(e) => setDeleteModal({ ...deleteModal, replacement: e.target.value })}>
                  <option value="none">Quitar etiqueta (vacío)</option>
                  {pageConfig[deleteModal.type === 'category' ? 'categories' : 'subCategories']
                    .filter(t => t !== deleteModal.target).map(t => <option key={t} value={t}>Reemplazar por: {t}</option>)}
                </select>
                <div className="flex gap-4">
                  <button onClick={() => setDeleteModal({ show: false })} className="flex-1 py-3 text-slate-400 font-black text-[10px] uppercase">Cancelar</button>
                  <button onClick={handleSmartDelete} className="flex-1 py-4 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase shadow-xl">Confirmar</button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {view === 'products' ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <form onSubmit={handleSubmitProduct} className="bg-white border border-gray-100 p-6 md:p-10 rounded-[2.5rem] shadow-sm md:sticky md:top-8 space-y-5">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-black italic text-[#a6192e] uppercase tracking-tighter">
                    {editingId ? 'Editando Insumo' : 'Editor de Stock'}
                  </h3>

                  {editingId && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm("¿Deseas salir de la edición? Los cambios no guardados se perderán.")) {
                          setForm({ name: '', description: '', categories: [], subCategories: [], variants: [] });
                          setEditingId(null);
                        }
                      }}
                      className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-red-600 transition-colors flex items-center gap-2 italic"
                    >
                      ✕ Cancelar Edición
                    </button>
                  )}
                </div>

                <input
                  placeholder="Nombre del Modelo"
                  className="w-full bg-gray-50 border border-gray-100 p-5 rounded-2xl outline-none text-sm font-bold uppercase text-slate-800 focus:border-[#a6192e]/30 transition-all"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value.toUpperCase() })}
                  required
                />

                <textarea
                  placeholder="Descripción..."
                  className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl h-24 outline-none text-xs italic resize-none text-slate-800 focus:border-[#a6192e]/30 transition-all"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                ></textarea>
                <div className="space-y-3">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Categorías</p>
                  <div className="flex flex-wrap gap-2">
                    {pageConfig.categories.map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleTag(form.categories || [], c, 'categories')}
                        className={`px-4 py-2 rounded-xl text-[9px] font-black border transition-all uppercase italic ${form.categories?.includes(c) ? 'bg-[#a6192e] border-[#a6192e] text-white shadow-lg shadow-red-100' : 'bg-gray-50 text-slate-400 border-gray-100'}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Subcategorías</p>
                  <div className="flex flex-wrap gap-2">
                    {pageConfig.subCategories.map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleTag(form.subCategories || [], s, 'subCategories')}
                        className={`px-4 py-2 rounded-xl text-[9px] font-black border transition-all uppercase italic ${form.subCategories?.includes(s) ? 'bg-[#a6192e] border-[#a6192e] text-white shadow-lg shadow-red-100' : 'bg-gray-50 text-slate-400 border-gray-100'}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-gray-50">
                  <div className="flex justify-between items-center text-slate-800">
                    <span className="text-[10px] font-black uppercase italic">Variantes</span>
                    <button type="button" onClick={() => setForm({ ...form, variants: [...form.variants, { color: '', images: [] }] })} className="bg-gray-100 w-8 h-8 rounded-xl font-black">+</button>
                  </div>
                  {form.variants.map((v, idx) => (
                    <div key={idx} className="p-5 bg-gray-50 rounded-[1.5rem] relative">
                      <button type="button" onClick={() => { const n = [...form.variants]; n.splice(idx, 1); setForm({ ...form, variants: n }) }} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 text-[10px]">×</button>
                      <input placeholder="Color" className="w-full bg-transparent border-b border-gray-200 mb-4 text-xs font-bold uppercase outline-none text-slate-800" value={v.color} onChange={e => { const n = [...form.variants]; n[idx].color = e.target.value; setForm({ ...form, variants: n }) }} />
                      <input type="file" multiple className="text-[9px] w-full" onChange={e => handleImageUpload(idx, e)} />
                      <div className="flex flex-wrap gap-2 mt-4">
                        {(v.images || []).map((img, i) => (
                          <div key={i} className="relative w-10 h-10 group/img">
                            <img src={img} className="w-full h-full object-cover rounded-xl" alt="" />
                            <button type="button" onClick={() => handleRemoveImage(idx, i, img)} className="absolute inset-0 bg-red-600/80 text-white opacity-0 group-hover/img:opacity-100 rounded-xl flex items-center justify-center text-xs">×</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <button disabled={loading} className="w-full py-5 bg-[#a6192e] text-white rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl">{loading ? 'Procesando...' : 'Publicar Insumo'}</button>
              </form>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <h3 className="text-xl font-black uppercase italic text-slate-800">Inventario Activo</h3>
              <div className="grid grid-cols-1 gap-4">
                {products.map(p => (
                  <div key={p.id} className="bg-white border border-gray-200 p-5 rounded-[2rem] flex items-center justify-between gap-6 hover:border-[#a6192e]/20 transition-all shadow-sm">
                    <div className="flex items-center gap-5">
                      <img src={p.variants?.[0]?.images?.[0] || 'https://via.placeholder.com/150'} className="w-16 h-16 rounded-2xl object-cover" alt="" />
                      <div>
                        <h4 className="font-black text-sm uppercase text-slate-800 italic">{p.name}</h4>
                        <div className="flex gap-2 mt-1">
                          {p.categories?.map(c => <span key={c} className="text-[7px] font-black bg-red-50 text-[#a6192e] px-2 py-0.5 rounded-full uppercase">{c}</span>)}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => { setEditingId(p.id); setForm(p); window.scrollTo(0, 0); }} className="px-6 py-3 bg-gray-50 text-[10px] font-black uppercase rounded-xl text-slate-800">Editar</button>
                      <button onClick={() => handleDeleteFullProduct(p)} className="px-6 py-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-600 hover:text-white transition-all text-sm font-bold">×</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-12 max-w-6xl text-slate-800">
            {/* WhatsApp */}
            <div className="bg-white border border-gray-200 p-8 md:p-12 rounded-[3rem] shadow-sm space-y-4">
              <h2 className="text-2xl font-black uppercase italic text-[#a6192e]">WhatsApp General</h2>
              <input className="w-full bg-gray-50 border border-gray-100 p-5 rounded-3xl outline-none text-sm font-bold text-slate-800" value={pageConfig.whatsappNumber} onChange={e => setPageConfig({ ...pageConfig, whatsappNumber: e.target.value })} />
            </div>

            {/* Banners */}
            <div className="bg-white border border-gray-200 p-6 md:p-12 rounded-[3rem] shadow-sm space-y-10 overflow-hidden">
              {/* Header del Editor */}
              <div className="flex justify-between items-center border-b border-gray-100 pb-10">
                <div className="space-y-1">
                  <h2 className="text-3xl font-black italic uppercase text-[#a6192e] leading-none tracking-tighter">Banners</h2>
                  <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest italic">Gestiona el impacto visual de tu Home</p>
                </div>
                <button
                  onClick={() => setPageConfig({ ...pageConfig, heroSlides: [...pageConfig.heroSlides, { id: Date.now(), background: '', title: 'NUEVO LANZAMIENTO', subtitle: 'Calidad industrial garantizada', buttonText: 'VER CATÁLOGO', link: '/productos' }] })}
                  className="bg-[#a6192e] text-white px-8 py-4 rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] shadow-xl hover:scale-105 transition-transform"
                >
                  + Nuevo Banner
                </button>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-12">
                {/* Lista de Edición */}
                <div className="space-y-6 max-h-[700px] overflow-y-auto pr-4 custom-scrollbar">
                  {pageConfig.heroSlides.map((slide, idx) => (
                    <div
                      key={slide.id}
                      draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(idx)}
                      className={`p-8 bg-gray-50 rounded-[2.5rem] border-2 cursor-move transition-all relative ${draggedIdx === idx ? 'opacity-30 border-dashed border-[#a6192e]' : 'border-gray-100 hover:border-[#a6192e]/20'}`}
                    >
                      <button
                        onClick={() => setPageConfig({ ...pageConfig, heroSlides: pageConfig.heroSlides.filter(s => s.id !== slide.id) })}
                        className="absolute top-6 right-8 text-red-600 text-[10px] font-black uppercase italic z-20 hover:scale-110 transition-transform"
                      >
                        Quitar Banner
                      </button>

                      <div className="space-y-5 pt-6">
                        {/* Título */}
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Título Principal (Impacto)</label>
                          <input
                            className="w-full bg-white border border-gray-100 p-4 rounded-xl text-xs font-black uppercase text-slate-800 focus:border-[#a6192e] outline-none transition-all"
                            value={slide.title}
                            onChange={e => updateSlide(idx, 'title', e.target.value.toUpperCase())}
                          />
                        </div>

                        {/* Subtítulo */}
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Subtítulo o Descripción corta</label>
                          <input
                            className="w-full bg-white border border-gray-100 p-4 rounded-xl text-xs font-bold text-slate-800 focus:border-[#a6192e] outline-none transition-all"
                            value={slide.subtitle}
                            onChange={e => updateSlide(idx, 'subtitle', e.target.value)}
                          />
                        </div>

                        {/* Botón y Link */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Texto del Botón</label>
                            <input
                              className="w-full bg-white border border-gray-100 p-4 rounded-xl text-xs font-black uppercase text-slate-800 focus:border-[#a6192e] outline-none transition-all"
                              value={slide.buttonText}
                              onChange={e => updateSlide(idx, 'buttonText', e.target.value.toUpperCase())}
                            />
                          </div>

                          <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 italic">Destino del Clic</label>
                            <select
                              className="w-full bg-white border border-gray-100 p-4 rounded-xl text-[10px] font-bold text-slate-800 outline-none focus:border-[#a6192e] transition-all cursor-pointer appearance-none"
                              value={slide.link}
                              onChange={e => updateSlide(idx, 'link', e.target.value)}
                            >
                              <optgroup label="Secciones">
                                <option value="#novedades">Novedades (Home)</option>
                                <option value="/nosotros">Nosotros</option>
                                <option value="/productos">Catálogo Completo</option>
                              </optgroup>
                              <optgroup label="Categorías de Productos">
                                {pageConfig.categories?.map(cat => (
                                  <option key={cat} value={`/productos?cat=${cat}`}>{cat}</option>
                                ))}
                              </optgroup>
                              <optgroup label="Subcategorías">
                                {pageConfig.subCategories?.map(sub => (
                                  <option key={sub} value={`/productos?sub=${sub}`}>{sub}</option>
                                ))}
                              </optgroup>
                            </select>
                          </div>
                        </div>

                        {/* Acciones de Imagen */}
                        <div className="grid grid-cols-2 gap-4 pt-2">
                          <button
                            onClick={() => setPreviewSlideIdx(idx)}
                            className={`rounded-xl text-[9px] font-black uppercase transition-all py-4 ${previewSlideIdx === idx ? 'bg-[#a6192e] text-white shadow-lg' : 'bg-white text-slate-400 border border-gray-100 hover:bg-gray-100'}`}
                          >
                            Visualizar
                          </button>
                          <label className="text-center bg-slate-900 text-white py-4 rounded-xl text-[9px] font-black cursor-pointer uppercase transition-all hover:bg-slate-800 shadow-lg">
                            <input type="file" className="hidden" onChange={e => handleImageUpload(null, e, true, idx)} />
                            Cambiar Imagen
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Preview Pegajoso */}
                <div className="flex flex-col items-center sticky top-10 h-fit bg-gray-50/50 p-8 rounded-[3rem] border border-gray-100">
                  <div className="flex gap-2 bg-gray-200 p-1.5 rounded-full w-fit mb-10 shadow-inner">
                    <button onClick={() => setPreviewDevice('desktop')} className={`px-8 py-2.5 rounded-full text-[10px] font-black uppercase transition-all ${previewDevice === 'desktop' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}>Desktop</button>
                    <button onClick={() => setPreviewDevice('mobile')} className={`px-8 py-2.5 rounded-full text-[10px] font-black uppercase transition-all ${previewDevice === 'mobile' ? 'bg-white text-[#a6192e] shadow-md' : 'text-slate-500 hover:text-slate-700'}`}>Móvil</button>
                  </div>

                  <div className={`relative bg-slate-900 overflow-hidden transition-all duration-700 shadow-[0_40px_80px_rgba(0,0,0,0.3)] ${previewDevice === 'mobile' ? 'w-[280px] h-[560px] rounded-[3.5rem] border-[12px] border-slate-800' : 'w-full aspect-video rounded-[2.5rem] border-[8px] border-slate-800'}`}>
                    <AnimatePresence mode="wait">
                      {pageConfig.heroSlides[previewSlideIdx] ? (
                        <motion.div
                          key={previewSlideIdx + previewDevice}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center"
                        >
                          {pageConfig.heroSlides[previewSlideIdx].background && (
                            <img src={pageConfig.heroSlides[previewSlideIdx].background} className="absolute inset-0 w-full h-full object-cover opacity-40 transition-transform duration-[10s] scale-110" alt="" />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/60" />

                          <div className="relative z-10 space-y-4">
                            <h4 className={`font-black italic uppercase leading-[0.9] text-white tracking-tighter drop-shadow-2xl ${previewDevice === 'mobile' ? 'text-3xl' : 'text-5xl'}`}>
                              {pageConfig.heroSlides[previewSlideIdx].title || 'SIN TÍTULO'}
                            </h4>
                            <p className="text-white/80 text-[10px] font-medium italic tracking-wide max-w-[200px] mx-auto leading-tight">
                              {pageConfig.heroSlides[previewSlideIdx].subtitle || 'Agrega una descripción para ver el preview'}
                            </p>
                            <div className="inline-block bg-[#a6192e] text-white font-black uppercase rounded-full px-8 py-3 mt-4 text-[9px] tracking-[0.2em] shadow-xl">
                              {pageConfig.heroSlides[previewSlideIdx].buttonText || 'BOTÓN'}
                            </div>
                          </div>
                        </motion.div>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-[10px] font-black uppercase italic">Selecciona un banner para previsualizar</div>
                      )}
                    </AnimatePresence>
                  </div>
                  <p className="mt-8 text-slate-400 text-[9px] font-black uppercase tracking-[0.2em] italic">Vista Previa Real-Time</p>
                </div>
              </div>
            </div>

            {/* Categorías y Subcategorías */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {['category', 'subCategory'].map((type) => (
                <div key={type} className="bg-white border border-gray-200 p-8 rounded-[3rem] shadow-sm">
                  <h2 className="text-2xl font-black mb-10 uppercase italic text-[#a6192e]">{type === 'category' ? 'Categorías' : 'Subcategorías'}</h2>
                  <div className="flex gap-3 mb-10">
                    <input
                      placeholder="Añadir..." className="flex-1 bg-gray-50 border border-gray-100 p-5 rounded-2xl outline-none text-sm font-bold uppercase text-slate-800"
                      value={type === 'category' ? newCatName : newSubName}
                      onChange={e => type === 'category' ? setNewCatName(e.target.value) : setNewSubName(e.target.value)}
                    />
                    <button onClick={() => {
                      const val = type === 'category' ? newCatName : newSubName;
                      if (!val.trim()) return;
                      const field = type === 'category' ? 'categories' : 'subCategories';
                      setPageConfig({ ...pageConfig, [field]: [...pageConfig[field], val.trim().toUpperCase()] });
                      type === 'category' ? setNewCatName('') : setNewSubName('');
                    }} className="bg-[#a6192e] text-white px-8 rounded-2xl font-bold">+</button>
                  </div>
                  <div className="flex flex-wrap gap-2.5">
                    {pageConfig[type === 'category' ? 'categories' : 'subCategories'].map(tag => (
                      <div key={tag} className="bg-gray-50 px-4 py-2.5 rounded-xl text-[10px] font-black border border-gray-100 flex items-center gap-4 text-slate-800">
                        {editMode.type === type && editMode.original === tag ? (
                          <input autoFocus className="bg-white border-b-2 border-[#a6192e] outline-none w-24 text-center uppercase" value={editMode.current} onChange={e => setEditMode({ ...editMode, current: e.target.value })} onBlur={() => handleEditName(type)} onKeyDown={e => e.key === 'Enter' && handleEditName(type)} />
                        ) : (
                          <><span onClick={() => setEditMode({ type, original: tag, current: tag })} className="cursor-pointer hover:text-[#a6192e] italic uppercase">{tag}</span><button onClick={() => setDeleteModal({ show: true, type, target: tag, replacement: 'none' })} className="text-red-500 font-bold text-sm">×</button></>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* FAQ */}
            <div className="bg-white border border-gray-200 p-8 md:p-12 rounded-[3rem] shadow-sm space-y-10">
              <h2 className="text-2xl font-black uppercase italic text-[#a6192e]">FAQ</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <input placeholder="Pregunta" className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none text-xs font-bold uppercase text-slate-800" value={newFAQ.question} onChange={e => setNewFAQ({ ...newFAQ, question: e.target.value })} />
                  <textarea placeholder="Respuesta" className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl h-24 outline-none text-xs italic resize-none text-slate-800" value={newFAQ.answer} onChange={e => setNewFAQ({ ...newFAQ, answer: e.target.value })} />
                  <button onClick={() => { if (!newFAQ.question || !newFAQ.answer) return; setPageConfig({ ...pageConfig, faqs: [...pageConfig.faqs, { ...newFAQ, id: Date.now() }] }); setNewFAQ({ question: '', answer: '' }) }} className="w-full py-4 bg-[#a6192e] text-white rounded-2xl font-black text-[10px] uppercase shadow-lg shadow-red-100">Añadir FAQ</button>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                  {pageConfig.faqs.map((f, i) => (
                    <div key={f.id} className="p-4 bg-gray-50 border border-gray-100 rounded-2xl flex justify-between items-start text-slate-800">
                      <div><p className="font-bold text-xs text-[#a6192e] uppercase italic mb-1 leading-none">{f.question}</p><p className="text-[10px] italic">{f.answer}</p></div>
                      <button onClick={() => setPageConfig(prev => ({ ...prev, faqs: prev.faqs.filter(item => item.id !== f.id) }))} className="text-red-400 hover:text-red-600 ml-4 font-bold">×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-white border border-gray-200 p-8 md:p-14 rounded-[4rem] shadow-sm space-y-12">
              <h2 className="text-3xl font-black uppercase italic text-[#a6192e] tracking-tighter leading-none">Footer Personalizado</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-5 bg-gray-50 p-6 rounded-[2.5rem] border border-gray-100">
                <input placeholder="Etiqueta" className="w-full bg-white border border-gray-100 p-4 rounded-xl text-xs font-bold uppercase text-slate-800 outline-none" value={newContact.title} onChange={e => setNewContact({ ...newContact, title: e.target.value })} />
                <select className="w-full bg-white border border-gray-100 p-4 rounded-xl text-[11px] font-bold text-slate-800 outline-none" value={newContact.icon} onChange={e => setNewContact({ ...newContact, icon: e.target.value })}>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="mail">E-mail</option>
                  <option value="phone">Teléfono</option>
                </select>
                <input placeholder="Dato Visible" className="w-full bg-white border border-gray-100 p-4 rounded-xl text-xs font-bold uppercase text-slate-800 outline-none" value={newContact.value} onChange={e => setNewContact({ ...newContact, value: e.target.value })} />
                <input placeholder="Link (Opcional)" className="w-full bg-white border border-gray-100 p-4 rounded-xl text-xs font-bold text-slate-800 outline-none" value={newContact.link} onChange={e => setNewContact({ ...newContact, link: e.target.value })} />
                <button onClick={() => { if (!newContact.title || !newContact.value) return; setPageConfig({ ...pageConfig, contacts: [...(pageConfig.contacts || []), { ...newContact, id: Date.now() }] }); setNewContact({ title: '', value: '', link: '', icon: 'whatsapp' }) }} className="md:col-span-4 py-4 bg-[#a6192e] text-white rounded-2xl font-black uppercase text-xs">Añadir al Footer</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {(pageConfig.contacts || []).map((c, idx) => (
                  <div key={c.id} className="flex justify-between items-center p-6 bg-white border border-gray-200 rounded-[2rem] shadow-sm text-slate-800 transition-all hover:border-[#a6192e]/40">
                    <div className="truncate pr-4"><p className="text-[9px] font-black text-[#a6192e] uppercase italic">{c.title} ({c.icon})</p><p className="text-xs font-bold uppercase truncate">{c.value}</p></div>
                    <button onClick={() => setPageConfig({ ...pageConfig, contacts: pageConfig.contacts.filter(item => item.id !== c.id) })} className="text-red-500 font-bold text-xl hover:scale-125 transition-transform">×</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Publicar */}
            <div className="bg-[#a6192e] p-12 md:p-16 rounded-[4rem] text-center shadow-2xl relative overflow-hidden group">
              <div className="relative z-10 space-y-8 text-white">
                <div className="space-y-3">
                  <h2 className="text-3xl md:text-5xl font-black uppercase italic tracking-tighter leading-none">Actualizar Sitio Web</h2>
                  <p className="text-red-100/60 font-black text-[10px] uppercase tracking-[0.4em] italic leading-none">Publicación instantánea</p>
                </div>
                <button onClick={saveGlobalConfig} className="w-full max-w-sm py-6 bg-white text-[#a6192e] rounded-full font-black uppercase text-xs tracking-[0.3em] hover:scale-[1.03] transition-all shadow-2xl active:scale-95">Sincronizar Cambios</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPortal;